/**
 * Configuración para el manejo de persistencia de sesión
 */

export type SessionStrategy =
  | "memory-only"
  | "memory-with-metadata"
  | "session-storage"
  | "local-storage";

export interface SessionConfig {
  strategy: SessionStrategy;
  maxSessionDuration: number; // en milisegundos
  inactivityTimeout: number; // en milisegundos
  tokenRefreshInterval: number; // en milisegundos
  enableActivityTracking: boolean;
  enableDebugMode: boolean;
}

/**
 * Configuración por defecto
 */
export const defaultSessionConfig: SessionConfig = {
  strategy: "memory-with-metadata", // Estrategia híbrida recomendada
  maxSessionDuration: 8 * 60 * 60 * 1000, // 8 horas
  inactivityTimeout: 30 * 60 * 1000, // 30 minutos
  tokenRefreshInterval: 30 * 60 * 1000, // 30 minutos
  enableActivityTracking: true,
  enableDebugMode: false, // Cambia a true para habilitar modo debug
};

/**
 * Configuraciones predefinidas para diferentes escenarios
 */
export const sessionConfigs = {
  // Máxima seguridad - solo memoria, sin persistencia
  maxSecurity: {
    ...defaultSessionConfig,
    strategy: "memory-only" as SessionStrategy,
    enableActivityTracking: false,
  },

  // Recomendado - memoria con metadatos mínimos
  recommended: {
    ...defaultSessionConfig,
    strategy: "memory-with-metadata" as SessionStrategy,
  },

  // Conveniencia - sessionStorage completo
  convenient: {
    ...defaultSessionConfig,
    strategy: "session-storage" as SessionStrategy,
    maxSessionDuration: 12 * 60 * 60 * 1000, // 12 horas
  },

  // Desarrollo - configuración para testing
  development: {
    ...defaultSessionConfig,
    strategy: "memory-with-metadata" as SessionStrategy,
    maxSessionDuration: 24 * 60 * 60 * 1000, // 24 horas
    inactivityTimeout: 2 * 60 * 60 * 1000, // 2 horas
    enableDebugMode: true,
  },
};

/**
 * Obtiene la configuración de sesión (configuración fija)
 */
export const getSessionConfig = (): SessionConfig => {
  // Configuración fija - cambiar aquí para modificar la estrategia
  return sessionConfigs.convenient; // Cambia esto por la configuración deseada
};

/**
 * Obtiene la configuración de MSAL cache basada en la estrategia
 */
export const getMsalCacheConfig = (strategy: SessionStrategy) => {
  switch (strategy) {
    case "memory-only":
    case "memory-with-metadata":
      return {
        cacheLocation: "memoryStorage" as const,
        storeAuthStateInCookie: true,
        secureCookies: true, // Cambia a false solo para desarrollo local HTTP
      };

    case "session-storage":
      // Usar localStorage en lugar de sessionStorage para compartir entre pestañas
      // Esto resuelve el problema en modo incógnito donde sessionStorage no se comparte
      return {
        cacheLocation: "localStorage" as const,
        storeAuthStateInCookie: true,
        secureCookies: true, // Cambia a false solo para desarrollo local HTTP
      };

    case "local-storage":
      return {
        cacheLocation: "localStorage" as const,
        storeAuthStateInCookie: true,
        secureCookies: true, // Cambia a false solo para desarrollo local HTTP
      };

    default:
      return {
        cacheLocation: "memoryStorage" as const,
        storeAuthStateInCookie: true,
        secureCookies: true, // Cambia a false solo para desarrollo local HTTP
      };
  }
};
